import { create } from 'zustand'
import {
  getFixedAssetSyncCheck,
  getIgnoredAccounts,
  updateIgnoredAccounts,
  type FixedAssetListItem,
  type SyncCheckResponse,
} from '@/services/fixedAssets'
import type { FixedAssetModalPrefill } from '@/pages/fixed-assets/FixedAssetModal'

interface FixedAssetsSyncState {
  errorCount: number
  latest: SyncCheckResponse | null
  fetchLatest: (refresh?: boolean) => Promise<void>
  setLatest: (check: SyncCheckResponse) => void
  ignoreAccounts: (accountIds: string[]) => Promise<void>
  assetModalOpen: boolean
  assetModalAsset: FixedAssetListItem | null
  assetModalPrefill: FixedAssetModalPrefill | undefined
  assetsRevision: number
  lastCreatedAssetId: number | null
  openCreateModal: (prefill?: FixedAssetModalPrefill) => void
  openEditModal: (asset: FixedAssetListItem) => void
  closeAssetModal: () => void
  notifyRegisterChanged: (createdId?: number) => void
}

export const useFixedAssetsSyncStore = create<FixedAssetsSyncState>((set) => ({
  errorCount: 0,
  latest: null,
  assetModalOpen: false,
  assetModalAsset: null,
  assetModalPrefill: undefined,
  assetsRevision: 0,
  lastCreatedAssetId: null,

  fetchLatest: async (refresh = false) => {
    try {
      const check = await getFixedAssetSyncCheck({ refresh })
      set({ latest: check, errorCount: check.error_count })
    } catch (err) {
      console.error('Failed to fetch fixed-assets sync check:', err)
    }
  },

  setLatest: (check) => set({ latest: check, errorCount: check.error_count }),

  ignoreAccounts: async (accountIds) => {
    const uniqueIds = [...new Set(accountIds.filter((id) => id.trim()))]
    if (uniqueIds.length === 0) return

    const current = await getIgnoredAccounts()
    const merged = [...new Set([...current.account_ids, ...uniqueIds])]
    await updateIgnoredAccounts(merged)
    const check = await getFixedAssetSyncCheck()
    set({ latest: check, errorCount: check.error_count })
  },

  openCreateModal: (prefill) =>
    set({
      assetModalOpen: true,
      assetModalAsset: null,
      assetModalPrefill: prefill,
    }),

  openEditModal: (asset) =>
    set({
      assetModalOpen: true,
      assetModalAsset: asset,
      assetModalPrefill: undefined,
    }),

  closeAssetModal: () =>
    set({
      assetModalOpen: false,
      assetModalAsset: null,
      assetModalPrefill: undefined,
    }),

  notifyRegisterChanged: (createdId) =>
    set((state) => ({
      assetsRevision: state.assetsRevision + 1,
      lastCreatedAssetId: createdId ?? null,
    })),
}))
